import { typography, radius } from '@/shared/theme/tokens';
import { REC_IMPACT_META, withAlpha } from '../../liveModeMeta';
import { BCAST, UP_NEXT_VISIBLE } from './broadcastMeta';

/**
 * "UP NEXT" — the rest of the ranked rec stack as a compact, scannable queue
 * under the anchored lower-third. Stable priority order (never a carousel) so a
 * glance is resumable. Overflow collapses into "+N MORE". Each row is the click
 * target → onSelect(rec). Hidden entirely when there is nothing queued.
 */
export function UpNextList({ recs = [], onSelect }) {
  const queued = recs.slice(1); // rankedRecs[0] is on air in the lower-third
  if (queued.length === 0) return null;

  const shown = queued.slice(0, UP_NEXT_VISIBLE);
  const overflow = queued.length - shown.length;
  const clickable = typeof onSelect === 'function';

  return (
    <div style={{
      flexShrink: 0, display: 'flex', flexDirection: 'column',
      background: BCAST.panel, border: `1px solid ${BCAST.line}`, borderRadius: radius.sm, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
        background: BCAST.chrome, borderBottom: `1px solid ${BCAST.line}`,
      }}>
        <span style={{ width: 3, height: 11, background: BCAST.brand, flexShrink: 0 }} />
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: BCAST.inkDim,
        }}>
          Up Next
        </span>
        <span style={{
          fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint, fontVariantNumeric: 'tabular-nums',
        }}>
          [{queued.length}]
        </span>
      </div>

      {shown.map((rec) => {
        const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
        const numerical = rec.action?.type === 'numerical';
        return (
          <div
            key={rec.id}
            onClick={clickable ? () => onSelect(rec) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rec); } } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            title={clickable ? `Open ${rec.parameterName} recommendation` : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
              borderBottom: `1px solid ${BCAST.line}`, cursor: clickable ? 'pointer' : 'default',
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: radius.full, background: impact.color, flexShrink: 0 }} />
            <span style={{
              flex: 1, minWidth: 0, fontFamily: BCAST.heading, fontSize: typography.fontSize.md,
              fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide,
              textTransform: 'uppercase', color: BCAST.ink,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {rec.parameterName}
            </span>
            <span style={{
              flexShrink: 0, fontFamily: BCAST.mono, fontSize: typography.fontSize.sm, color: BCAST.inkDim,
              fontVariantNumeric: 'tabular-nums', letterSpacing: typography.letterSpacing.wide,
            }}>
              {numerical
                ? `${rec.action.currentValue} → ${rec.action.recommendedValue}${rec.unit ? ` ${rec.unit}` : ''}`
                : 'ADJUST'}
            </span>
          </div>
        );
      })}

      {overflow > 0 && (
        <div style={{
          padding: '7px 14px', fontFamily: BCAST.mono, fontSize: typography.fontSize.xs,
          color: BCAST.brand, background: withAlpha(BCAST.brand, '0d'),
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums',
        }}>
          +{overflow} MORE IN QUEUE
        </div>
      )}
    </div>
  );
}
